import React, {
  type PropsWithChildren,
  useRef,
  useEffect,
  useMemo,
} from "react";

import clsx from "clsx";
import { atom, useAtom } from "jotai";
import { ScopeProvider } from "jotai-scope";
import { isEqual } from "lodash";
import { twMerge } from "tailwind-merge";
import { useHover } from "usehooks-ts";

export type FlatSelectOption<T> = {
  value: T;
  label: string;
  onClick?: () => void;
  renderOption?: ({
    isSelected,
  }: {
    isSelected: boolean;
  }) => React.ReactElement;
};

type Props<T> = {
  className?: string;
  optionClassName?: string;
  activeOptionClassName?: string;
  options: FlatSelectOption<T>[];
  selected: FlatSelectOption<T>;
  setSelected: (v: FlatSelectOption<T>) => void;
};

const hoveredOptionAtom = atom<number>(0);
export const useHoveredOption = () => useAtom(hoveredOptionAtom);

export const AtomsProvider = ({ children }: PropsWithChildren<unknown>) => {
  return <ScopeProvider atoms={[hoveredOptionAtom]}>{children}</ScopeProvider>;
};

export function FlatSelect<T>(props: Props<T>) {
  return (
    <AtomsProvider>
      <LocalFlatSelect {...props} />
    </AtomsProvider>
  );
}

function LocalFlatSelect<T>({
  className,
  optionClassName,
  activeOptionClassName,
  options,
  selected,
  setSelected,
}: Props<T>) {
  return (
    <div className={twMerge("flex text-xs h-8", className)}>
      {options.map((e, index) => (
        <SelectOption
          e={e}
          optionClassName={optionClassName}
          activeOptionClassName={activeOptionClassName}
          index={index}
          key={index}
          selected={selected}
          setSelected={setSelected}
        />
      ))}
    </div>
  );
}

function SelectOption<T>({
  selected,
  setSelected,
  e,
  index,
  optionClassName,
  activeOptionClassName,
}: {
  index: number;
  selected: FlatSelectOption<T>;
  e: FlatSelectOption<T>;
  setSelected: (e: FlatSelectOption<T>) => void;
  optionClassName?: string;
  activeOptionClassName?: string;
}) {
  const hoverRef = useRef(null);
  const [hoveredState, setHovered] = useHoveredOption();

  const isHover = useHover(hoverRef);

  useEffect(() => {
    if (isHover) {
      setHovered(index);
    } else {
      setHovered(-100);
    }
  }, [index, isHover, setHovered]);

  const previousSiblingIsHovered = useMemo(() => {
    if (hoveredState === index - 1) {
      return true;
    }
    return false;
  }, [hoveredState, index]);

  const equalsTo = isEqual(selected.value, e.value);
  return (
    <div
      ref={hoverRef}
      className={twMerge(
        clsx(
          "first:rounded-l-md block border cursor-pointer",
          "last:border-r-[1px] border-r-0 px-1 last:rounded-r-md",
          equalsTo &&
            twMerge("bg-v2-action border-v2-action", activeOptionClassName),
          {
            "hover:bg-cosmic-4 hover:border-cosmic+1 bg-v2-primary border-v2-secondary":
              !equalsTo,
          },
          { "border-l-cosmic+1": previousSiblingIsHovered },
        ),
        optionClassName,
      )}
      onClick={() => {
        setSelected(e);
        e.onClick?.();
      }}
    >
      {e.renderOption ? (
        e.renderOption({ isSelected: equalsTo })
      ) : (
        <button
          className={clsx("h-full text-center w-full", {
            "text-white": equalsTo,
          })}
        >
          {e.label}
        </button>
      )}
    </div>
  );
}
